/**
 * PsicoSalud — Indicadores de apoyo a la decisión (Módulo 5).
 *
 * Toma las aplicaciones de escala registradas en el historial, las envía
 * al servicio de análisis y presenta la evolución junto al Índice de
 * Cambio Confiable, que indica si la variación observada supera el error
 * de medición del instrumento o si podría ser ruido.
 *
 * La banda ética pinned al tope de la página es el cumplimiento explícito
 * del requisito de Cap. IV: «toda salida del componente de análisis lleva
 * descargo ético». Aunque el profesional scrollee, la banda lo acompaña.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import ScoreTrendChart from '@/components/ScoreTrendChart';
import { formatMeasurementDate } from '@/utils/measurementDate';
import { aiApi, appointmentsApi, clinicalRecordsApi, formsApi, patientsApi } from '@/api';
import { useAuth } from '@/context/AuthContext';
import type {
  Appointment,
  AlertEvaluation,
  ClinicalRecord,
  FormResponseResult,
  Patient,
  TrendAnalysis,
} from '@/types';

const SCALES = ['PHQ-9', 'GAD-7'] as const;
const REMISSION_CUTOFF: Record<string, number> = { 'PHQ-9': 5, 'GAD-7': 5 };

const SEVERITY_BADGE: Record<string, string> = {
  INFO: 'badge-info',
  WARNING: 'badge-warning',
  CRITICAL: 'badge-critical',
};

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const { patientId = '' } = useParams();
  const { user } = useAuth();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [responses, setResponses] = useState<FormResponseResult[]>([]);
  // El motor de alertas evalúa cuatro familias de reglas y la pantalla solo
  // le enviaba los puntajes de escala. Las otras tres —indicadores de riesgo
  // de la nota, cambio afectivo e inasistencias— no podían dispararse nunca
  // (defecto A6, del que la evaluación señaló la primera).
  const [records, setRecords] = useState<ClinicalRecord[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [scale, setScale] = useState<string>(SCALES[0]);
  const [trend, setTrend] = useState<TrendAnalysis | null>(null);
  const [alerts, setAlerts] = useState<AlertEvaluation | null>(null);
  const [loading, setLoading] = useState(true);
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const [detail, list, history, agenda] = await Promise.all([
          patientsApi.get(patientId),
          formsApi.patientResponses(patientId),
          clinicalRecordsApi.patientHistory(patientId),
          appointmentsApi.list({ patient_id: patientId }),
        ]);
        setPatient(detail);
        setResponses(list);
        setRecords(history);
        setAppointments(agenda);
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [patientId]);

  // Aplicaciones completadas de la escala elegida, en orden cronológico:
  // el servicio necesita la serie ordenada para ajustar la tendencia y
  // calcular el RCI contra la línea base correcta.
  //
  // Se ordena por `completed_at` y no por la fecha recortada a día: dos
  // aplicaciones del mismo día empatan al comparar solo la fecha, y el
  // orden queda a merced del que devuelva la consulta. Todo lo que dependa
  // de «la última» —el puntaje actual y la bandera de ítem crítico— tiene
  // que salir de la misma lista o terminan describiendo aplicaciones
  // distintas en la misma alerta.
  const completedResponses = useMemo(
    () =>
      responses
        .filter((r) => r.template_code === scale && r.scores && r.completed_at)
        .sort((a, b) => a.completed_at!.localeCompare(b.completed_at!)),
    [responses, scale],
  );

  const measurements = useMemo(
    () =>
      completedResponses.map((r) => ({
        fecha: r.completed_at!.slice(0, 10),
        puntaje: r.scores!.total_score,
      })),
    [completedResponses],
  );

  // De la última aplicación salen la severidad y la bandera de ítem
  // crítico, que el puntaje por sí solo no expresa.
  const lastResponse = completedResponses[completedResponses.length - 1] ?? null;

  // Señales del análisis de las notas de sesión. La regla que alcanza nivel
  // crítico depende de estas, y el payload no las incluía.
  const noteSignals = useMemo(() => {
    const analizadas = records
      .flatMap((record) => record.notes)
      .filter((note) => note.ai_analysis_results)
      .sort((a, b) => b.created_at.localeCompare(a.created_at));

    const ultima = analizadas[0]?.ai_analysis_results ?? null;
    const previa = analizadas[1]?.ai_analysis_results ?? null;

    return {
      indicadores_riesgo_activos: ultima
        ? [...new Set(ultima.indicadores_riesgo.indicadores_activos.map((i) => i.indicador))]
        : [],
      puntaje_indicadores_riesgo: ultima?.indicadores_riesgo.puntaje_indicadores ?? 0,
      polaridad_actual: ultima?.analisis_afectivo.polaridad ?? 0,
      polaridad_previa: previa?.analisis_afectivo.polaridad ?? null,
    };
  }, [records]);

  // Señales de agenda: adherencia al tratamiento.
  const agendaSignals = useMemo(() => {
    const ahora = Date.now();
    const pasadas = appointments
      .filter((cita) => new Date(cita.start_time).getTime() <= ahora)
      .sort((a, b) => b.start_time.localeCompare(a.start_time));

    // Se cuenta hacia atrás desde la más reciente. Una cancelación no es una
    // inasistencia: no suma ni corta la racha.
    let consecutivas = 0;
    for (const cita of pasadas) {
      if (cita.status === 'NO_SHOW') consecutivas += 1;
      else if (cita.status === 'COMPLETED') break;
    }

    const ultimaAtendida = pasadas.find((cita) => cita.status === 'COMPLETED');
    const dias = ultimaAtendida
      ? Math.floor((ahora - new Date(ultimaAtendida.start_time).getTime()) / 86_400_000)
      : 0;

    return { citas_perdidas_consecutivas: consecutivas, dias_desde_ultima_sesion: dias };
  }, [appointments]);

  const analyze = useCallback(async () => {
    setAiError(null);
    try {
      // La tendencia necesita al menos dos mediciones; las alertas no.
      // Abandonar aquí dejaba sin evaluar a un paciente con una sola
      // aplicación, que es justo cuando una bandera de ítem crítico importa.
      let result: TrendAnalysis | null = null;
      if (measurements.length >= 2) {
        result = await aiApi.trend({ patient_id: patientId, escala: scale, mediciones: measurements });
      }
      setTrend(result);

      const latest = measurements[measurements.length - 1];
      const previous = measurements[measurements.length - 2];

      setAlerts(
        await aiApi.evaluateAlerts({
          patient_id: patientId,
          psychologist_id: user?.id ?? '',
          ...noteSignals,
          ...agendaSignals,
          escalas: latest
            ? [
                {
                  escala: scale,
                  puntaje_actual: latest.puntaje,
                  puntaje_previo: previous?.puntaje ?? null,
                  severidad: lastResponse?.scores?.severity,
                  cambio_confiable: result?.cambio_confiable.interpretacion,
                  requiere_revision_inmediata:
                    lastResponse?.scores?.requires_immediate_review ?? false,
                  items_criticos: (lastResponse?.scores?.alerts ?? [])
                    .filter((alerta) => alerta.type === 'ITEM_CRITICO')
                    .map((alerta) => alerta.label),
                },
              ]
            : [],
        }),
      );
    } catch {
      setAiError(t('analytics.serviceUnavailable'));
      setTrend(null);
      setAlerts(null);
    }
  }, [
    measurements,
    patientId,
    scale,
    user?.id,
    t,
    noteSignals,
    agendaSignals,
    lastResponse,
  ]);

  useEffect(() => {
    void analyze();
  }, [analyze]);

  if (loading) return <div className="empty-state">{t('common.loading')}</div>;

  return (
    <>
      <header className="page-header with-flex">
        <div>
          <span className="page-eyebrow">{t('analytics.title')}</span>
          <h1>{t('analytics.title')}</h1>
          <p className="page-descripcion">
            {patient ? (
              <>
                <Link
                  to={`/pacientes/${patientId}`}
                  style={{ color: 'var(--principal)', fontWeight: 'var(--peso-medio)' }}
                >
                  {patient.full_name}
                </Link>
                <span className="t-numerico"> · {patient.document_id}</span>
              </>
            ) : (
              <span>{t('common.loading')}</span>
            )}
          </p>
        </div>
        <div className="page-acciones">
          <Link
            to={`/pacientes/${patientId}/evaluaciones`}
            className="is-button is-secondary is-pequeno"
          >
            {t('forms.title')} →
          </Link>
        </div>
      </header>

      {/* Banda ética pinned — siempre visible. Es la materialización del
          requisito de Cap. IV sobre descargo ético en toda salida del
          componente de análisis. */}
      <aside className="ethics-band" role="note" aria-label={t('analytics.ethicsBand.title')}>
        <span className="ethics-band-marca">{t('analytics.ethicsBand.tag')}</span>
        <div className="ethics-band-texto">
          <strong>{t('analytics.ethicsBand.title')}</strong>{' '}
          {t('analytics.ethicsBand.body')}
        </div>
      </aside>

      <div className="card">
        <div className="form-row" style={{ marginBottom: 0 }}>
          <div className="field" style={{ marginBottom: 0, maxWidth: '260px' }}>
            <label htmlFor="scale-select">{t('analytics.selectScale')}</label>
            <select
              id="scale-select"
              value={scale}
              onChange={(event) => setScale(event.target.value)}
              data-cy="analytics-scale"
            >
              {SCALES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {aiError && <div className="notice notice-warning">{aiError}</div>}

      {measurements.length < 2 ? (
        <div className="card">
          <p className="empty-state" data-cy="analytics-insufficient">
            {t('analytics.noMeasurements')}
          </p>
        </div>
      ) : (
        trend && (
          <>
            <div className="card">
              <div className="card-header">
                <div>
                  <span className="card-eyebrow">{t('analytics.trend')}</span>
                  <h2>
                    {trend.escala}{' '}
                    <span className="t-numerico" style={{ color: 'var(--tinta-suave)' }}>
                      · {completedResponses.length} aplicaciones
                    </span>
                  </h2>
                </div>
              </div>

              <ScoreTrendChart
                measurements={trend.serie}
                maxScore={
                  responses.find((r) => r.template_code === scale && r.scores)?.scores?.max_score ??
                  (scale === 'PHQ-9' ? 27 : 21)
                }
                remissionCutoff={REMISSION_CUTOFF[scale]}
                scaleCode={trend.escala}
              />

              <div className="grid grid-3" style={{ marginTop: 'var(--espacio-5)' }}>
                <div className="card is-anidada" style={{ marginTop: 0, padding: 'var(--espacio-4)' }}>
                  <div className="stat-value t-numerico">{trend.puntaje_inicial}</div>
                  <div className="stat-label">{t('analytics.baseline')}</div>
                </div>
                <div className="card is-anidada" style={{ marginTop: 0, padding: 'var(--espacio-4)' }}>
                  <div className="stat-value t-numerico" data-cy="analytics-current">
                    {trend.puntaje_actual}
                  </div>
                  <div className="stat-label">{t('analytics.current')}</div>
                </div>
                <div className="card is-anidada" style={{ marginTop: 0, padding: 'var(--espacio-4)' }}>
                  <div className="stat-value t-numerico">
                    {trend.cambio_absoluto > 0 ? '+' : ''}
                    {trend.cambio_absoluto}
                  </div>
                  <div className="stat-label">
                    {t('analytics.change')} ({trend.cambio_porcentual}%)
                  </div>
                </div>
              </div>

              <div className="table-wrapper" style={{ marginTop: 'var(--espacio-4)' }}>
                <table>
                  <thead>
                    <tr>
                      <th>{t('common.date')}</th>
                      <th style={{ width: '8rem' }}>{trend.escala}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trend.serie.map((point) => (
                      <tr key={point.fecha}>
                        <td className="t-numerico">
                          {formatMeasurementDate(point.fecha)}
                        </td>
                        <td className="t-numerico">{point.puntaje}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <div>
                  <span className="card-eyebrow">{t('analytics.reliableChange')}</span>
                  <h2>{t('analytics.reliableChange')}</h2>
                </div>
                <span
                  className={`badge ${
                    trend.cambio_confiable.es_confiable ? 'badge-info' : 'badge-neutral'
                  }`}
                  data-cy="analytics-rci"
                >
                  RCI <span className="t-numerico">{trend.cambio_confiable.rci}</span>
                </span>
              </div>

              <p>
                {trend.cambio_confiable.es_confiable
                  ? t('analytics.reliableYes')
                  : t('analytics.reliableNo')}{' '}
                <span className="t-pequeno t-suave">
                  (se requieren {trend.cambio_confiable.puntos_necesarios_para_cambio_confiable}{' '}
                  puntos · {trend.cambio_confiable.metodo})
                </span>
              </p>

              <div className="button-row">
                <span
                  className={`badge ${
                    trend.criterios_clinicos.respuesta_al_tratamiento
                      ? 'badge-success'
                      : 'badge-neutral'
                  }`}
                >
                  {t('analytics.response')}:{' '}
                  {trend.criterios_clinicos.respuesta_al_tratamiento ? t('common.yes') : t('common.no')}
                </span>
                <span
                  className={`badge ${
                    trend.criterios_clinicos.remision ? 'badge-success' : 'badge-neutral'
                  }`}
                >
                  {t('analytics.remission')}:{' '}
                  {trend.criterios_clinicos.remision ? t('common.yes') : t('common.no')}
                </span>
                <span className="badge badge-neutral">
                  {t(`analytics.trends.${trend.tendencia.direccion}`, {
                    defaultValue: trend.tendencia.direccion,
                  })}
                </span>
              </div>

              <div className="ai-disclaimer">
                <span className="ai-disclaimer-titulo">{t('analytics.ethicsBand.tag')}</span>
                {trend.disclaimer}
              </div>
            </div>
          </>
        )
      )}

      {alerts && (
        <div className="card">
          <div className="card-header">
            <div>
              <span className="card-eyebrow">{t('analytics.alerts')}</span>
              <h2>{t('analytics.alerts')}</h2>
            </div>
            {alerts.severidad_maxima && (
              <span className={`badge ${SEVERITY_BADGE[alerts.severidad_maxima]}`}>
                {t(`analytics.severities.${alerts.severidad_maxima}`)}
              </span>
            )}
          </div>

          {alerts.alertas.length === 0 ? (
            <p className="empty-state" data-cy="analytics-no-alerts">
              {t('analytics.noAlerts')}
            </p>
          ) : (
            <ul className="button-row" data-cy="analytics-alerts" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 'var(--espacio-3)' }}>
              {alerts.alertas.map((alert, index) => (
                <li
                  key={`${alert.alert_type}-${index}`}
                  className="card is-anidada"
                  style={{ margin: 0, padding: 'var(--espacio-3) var(--espacio-4)' }}
                >
                  <span className={`badge ${SEVERITY_BADGE[alert.severity]}`}>
                    {t(`analytics.severities.${alert.severity}`)}
                  </span>{' '}
                  <span>{alert.message}</span>
                  <div className="t-pequeno t-suave" style={{ marginTop: 'var(--espacio-1)' }}>
                    {alert.motivo}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="ai-disclaimer">
            <span className="ai-disclaimer-titulo">{t('analytics.ethicsBand.tag')}</span>
            {alerts.disclaimer}
          </div>
        </div>
      )}
    </>
  );
}
